/**
 * Stable pastel assignment per interviewer for Schedule Interview (Details).
 * Mirrors KInterviewScheduler `SchInterviewerField` color map + unique-assignment.
 *
 * The store survives across interviewer add/remove events so an already-tinted
 * user keeps the same pastel. New users are given the first pastel that isn't
 * currently in use so two interviewers never collide on the same colour.
 */

const colorKeyByUserId: Record<number, string> = {};

// pastel-5 (#F3F4F9 / #585281) reads as a washed-out grey against the
// other tints, so it's excluded from the interviewer rotation.
const PASTEL_POOL_DEFAULT = [1, 2, 3, 4, 6, 7, 8, 9, 10] as const;
const PASTEL_POOL_NO_2 = [1, 3, 4, 6, 7, 8, 9, 10] as const;

function sortedFiniteIds(ids: number[]): number[] {
  return Array.from(new Set(ids))
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);
}

function pickFirstUnusedPastel(
  used: Set<string>,
  pool: readonly number[]
): string {
  for (const n of pool) {
    const key = `pastel-${n}`;
    if (!used.has(key)) {
      return key;
    }
  }
  // Pool exhausted — fall back to the first pastel so we never crash the UI.
  return `pastel-${pool[0]}`;
}

/**
 * Updates the module map for the current interviewer set (call before calendar + select render).
 * @param hasCandidateAvailability — when true, avoid assigning `pastel-2` (legacy parity).
 */
export function syncScheduleInterviewerPastelKeys(
  userIds: number[],
  hasCandidateAvailability = false
): void {
  const current = sortedFiniteIds(userIds);
  const currentSet = new Set(current);

  Object.keys(colorKeyByUserId).forEach((idStr) => {
    const id = Number(idStr);
    if (!currentSet.has(id)) {
      delete colorKeyByUserId[id];
    }
  });

  const pool = hasCandidateAvailability
    ? PASTEL_POOL_NO_2
    : PASTEL_POOL_DEFAULT;

  // First, reconcile existing assignments that are now illegal (e.g.
  // candidate availability appeared and someone was on pastel-2).
  for (const idNum of current) {
    const existing = colorKeyByUserId[idNum];
    if (!existing) continue;
    if (hasCandidateAvailability && existing === 'pastel-2') {
      delete colorKeyByUserId[idNum];
    }
  }

  // Assign unique pastels to anyone without a valid one. Deterministic
  // order (sorted ids) so two add-sequences converge to the same mapping.
  for (const idNum of current) {
    if (colorKeyByUserId[idNum]) continue;
    const used = new Set(Object.values(colorKeyByUserId));
    colorKeyByUserId[idNum] = pickFirstUnusedPastel(used, pool);
  }
}

/** Pastel key like `pastel-3` for chips / calendar after {@link syncScheduleInterviewerPastelKeys}. */
export function getScheduleInterviewerPastelKey(userId: number): string {
  if (!Number.isFinite(userId) || userId <= 0) {
    return 'pastel-1';
  }
  return colorKeyByUserId[userId] ?? 'pastel-1';
}

export function pastelNumberFromKey(key: string): number {
  const m = /^pastel-(\d+)$/.exec(key);
  const n = m ? Number(m[1]) : 1;
  if (!Number.isFinite(n) || n < 1 || n > 10) {
    return 1;
  }
  return n;
}
